// Selecting lines by pressing and dragging in the diff's line-number gutter:
// what a press does to the selection, which line is under the pointer, and
// how fast to scroll while the pointer is outside the scrolling frame.
import type { LineSelection } from '../state/anchor';

type Side = 'LEFT' | 'RIGHT';

export interface Press {
  path: string;
  side: Side;
  line: number;
  shift: boolean;
}

/**
 * The selection after a press on a gutter line, and whether a drag starts.
 * Shift extends a selection on the same side and file. A plain press on the
 * sole line of the selection clears it, unless a composer is open on it
 * (clearing would drop the draft). Any other press selects just that line,
 * which collapses a range pressed inside.
 */
export function gutterPress(current: LineSelection | null, p: Press): { selection: LineSelection | null; dragging: boolean } {
  const same = !!current && current.side === p.side && current.path === p.path;
  if (p.shift && same) return { selection: { ...current, end: p.line, dragging: true }, dragging: true };
  if (same && !p.shift && current.start === current.end && current.start === p.line && !current.dragging) {
    return { selection: null, dragging: false };
  }
  return { selection: { path: p.path, side: p.side, start: p.line, end: p.line, dragging: true }, dragging: true };
}

/** The extent of one gutter cell on screen (a wrapped line is one tall cell). */
export interface CellBox {
  line: number;
  top: number;
  bottom: number;
}

/**
 * The line whose cell contains `y`, else the nearest cell's line; null with
 * no cells. `at(i)` is the i-th of `count` cells, in document order, so
 * tops and bottoms only grow: a binary search reads about log2(count) cells.
 */
export function lineAtY(count: number, at: (i: number) => CellBox, y: number): number | null {
  if (count === 0) return null;
  // The first cell that ends below y.
  let lo = 0;
  let hi = count;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (at(mid).bottom > y) hi = mid;
    else lo = mid + 1;
  }
  if (lo === count) return at(count - 1).line;
  const next = at(lo);
  if (lo === 0 || next.top <= y) return next.line;
  const prev = at(lo - 1);
  return y - prev.bottom < next.top - y ? prev.line : next.line;
}

const MAX_STEP = 40;

/**
 * Pixels to scroll per frame for a pointer at `y` against a frame spanning
 * `top` to `bottom`: negative at or above the top edge, positive at or below the bottom edge, zero inside, growing
 * with the distance outside and capped.
 */
export function autoScrollDelta(y: number, top: number, bottom: number): number {
  if (y > top && y < bottom) return 0;
  const up = y <= top;
  const out = Math.abs(up ? y - top : y - bottom);
  return (up ? -1 : 1) * Math.min(MAX_STEP, 2 + out * 0.25);
}

const INSET = 2;

/**
 * The part of a scroller (its client rect) the reader can see in a window
 * `viewportHeight` tall under a top bar `topInset` tall, inset by 2 px on
 * both edges: the pointer's last position is the window's height minus one,
 * so the window's own edge needs the inset to be passed. A scroller taller
 * than the window has the window's edges as its own; one wholly out of view
 * collapses to the window edge nearest it.
 */
export function visibleFrame(rect: { top: number; bottom: number }, viewportHeight: number, topInset: number): { top: number; bottom: number } {
  const top = Math.max(rect.top, topInset) + INSET;
  const bottom = Math.min(rect.bottom, viewportHeight) - INSET;
  if (top <= bottom) return { top, bottom };
  // Out of view: the edge it lies beyond, so the pointer reaching that edge scrolls toward it.
  return rect.top > topInset ? { top: bottom, bottom } : { top, bottom: top };
}

/**
 * The cells of `side` under `root` in document order, listed again whenever
 * the diff re-renders them (a highlight landing, a block folding), so a
 * lookup never reads cells that left the page. `stop` ends the watching.
 */
export function watchCells(root: HTMLElement, side: Side): { cells: () => HTMLElement[]; stop: () => void } {
  let list: HTMLElement[] | null = null;
  const observer = new MutationObserver(() => (list = null));
  observer.observe(root, { childList: true, subtree: true });
  return {
    cells: () => {
      // Changes not yet delivered to the observer count too.
      if (observer.takeRecords().length) list = null;
      return (list ??= [...root.querySelectorAll<HTMLElement>(`.source-cell[data-side="${side}"][data-line]`)]);
    },
    stop: () => observer.disconnect(),
  };
}
